import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, ShoppingCart, Sun, Moon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { languages, type LanguageCode } from '@/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { FlagIcon } from '@/components/FlagIcon';
import psSymbol from '@/assets/ps-symbol.svg';

export function Header() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { lang, changeLanguage, getLocalizedPath } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (mobileMenuOpen && mobileMenuRef.current) {
      const firstLink = mobileMenuRef.current.querySelector('a');
      firstLink?.focus();
    }
  }, [mobileMenuOpen]);

  const currentLanguage = languages.find((l) => l.code === lang) || languages[0];

  const handleLanguageChange = (code: LanguageCode) => {
    changeLanguage(code);
  };

  const navigationItems = [
    { href: getLocalizedPath('/datafiler'), label: t('common.dataFiles') },
    
    { href: getLocalizedPath('/om-oss'), label: t('common.about') },
    { href: getLocalizedPath('/kontakt'), label: t('common.contact') },
    { href: getLocalizedPath('/geposit'), label: t('common.geposit') },
  ];

  const informationItems = [
    { href: getLocalizedPath('/information/data-i-vara-tjanster'), label: t('navigation.dataInServices') },
    { href: getLocalizedPath('/information/postnummer'), label: t('navigation.postalCodes') },
    { href: getLocalizedPath('/information/kopvillkor'), label: t('navigation.terms') },
    { href: getLocalizedPath('/information/faq'), label: t('navigation.faq') },
    { href: getLocalizedPath('/information/nyheter'), label: t('navigation.news') },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to={getLocalizedPath('/')} className="flex items-center gap-2 shrink-0">
          <img src={psSymbol} alt="Postnummerservice" width={36} height={36} className="h-9 w-9 rounded-full object-cover" loading="eager" fetchPriority="high" />
          <span className="text-lg font-semibold text-foreground hidden sm:inline">Postnummerservice</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                  <Link to={getLocalizedPath('/datafiler')}>
                    {t('common.dataFiles')}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>


              <NavigationMenuItem>
                <NavigationMenuTrigger>{t('common.information')}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[300px] gap-1 p-2">
                    {informationItems.map((item) => (
                      <li key={item.href}>
                        <NavigationMenuLink asChild className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <Link to={item.href}>
                            <span className="text-sm font-medium">{item.label}</span>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                  <Link to={getLocalizedPath('/om-oss')}>
                    {t('common.about')}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                  <Link to={getLocalizedPath('/kontakt')}>
                    {t('common.contact')}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                  <Link to={getLocalizedPath('/geposit')}>
                    {t('common.geposit')}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        {/* Right side: Theme + Language + Cart */}
        <div className="flex items-center space-x-1 shrink-0">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={theme === 'dark' ? 'Byt till ljust läge' : 'Byt till mörkt läge'}
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" aria-label={t('common.changeLanguage', 'Byt språk')}>
                <FlagIcon code={lang} />
                <span className="hidden sm:inline">{currentLanguage.name}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map((language) => (
                <DropdownMenuItem
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={cn("gap-2", lang === language.code && 'bg-accent')}
                >
                  <FlagIcon code={language.code} />
                  {language.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Webbshop CTA Button */}
          <Link to={getLocalizedPath('/shop')}>
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              {t('common.shop')}
            </Button>
          </Link>

          {/* Cart Button */}
          <Link to={getLocalizedPath('/cart')}>
            <Button variant="ghost" size="icon" className="relative" aria-label={t('common.cart', 'Kundvagn')}>
              <ShoppingCart className="h-5 w-5" />
            </Button>
          </Link>

          {/* Mobile menu button */}
          <Button
            ref={mobileMenuButtonRef}
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('common.closeMenu', 'Stäng meny') : t('common.openMenu', 'Öppna meny')}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border" ref={mobileMenuRef}>
          <nav className="container py-4 space-y-2" aria-label={t('common.mainNavigation', 'Huvudnavigering')}>
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="block px-4 py-2 text-sm font-medium rounded-md hover:bg-accent"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="px-4 py-2">
              <p className="text-xs text-muted-foreground mb-2">{t('common.information')}</p>
              {informationItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block px-2 py-1.5 text-sm rounded-md hover:bg-accent"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="border-t border-border mt-2 pt-2">
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium rounded-md hover:bg-accent"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === 'dark' ? t('common.lightMode', 'Ljust läge') : t('common.darkMode', 'Mörkt läge')}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}